import React from 'react';
import { 
  Box, 
  Card, 
  CardContent, 
  Typography, 
  Button, 
  Paper,
  Grid,
  Divider,
  useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Link } from 'react-router-dom';
import ConstructionIcon from '@mui/icons-material/Construction';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { getAnalytics, logEvent } from 'firebase/analytics';

/**
 * ComingSoonCalculator component
 * Displays a placeholder for calculators that are still in development
 * 
 * @param {Object} props
 * @param {string} props.calculatorName - Name of the calculator
 * @param {string} props.description - Description of the calculator's purpose
 * @param {string} props.category - Category the calculator belongs to (financial, math, fitness, other)
 * @param {string} props.expectedRelease - Expected release timeframe (e.g., "Summer 2025")
 * @param {Array} props.features - Array of features to be included in the calculator
 * @param {Array} props.relatedCalculators - Array of related calculators with name and path
 */
const ComingSoonCalculator = ({ 
  calculatorName, 
  description, 
  category = 'financial',
  expectedRelease = 'Coming Soon', 
  features = [], 
  relatedCalculators = [] 
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Log page view to analytics
  React.useEffect(() => {
    try {
      const analytics = getAnalytics();
      logEvent(analytics, 'coming_soon_view', {
        calculator_name: calculatorName,
        calculator_category: category
      });
    } catch (error) {
      console.error('Analytics error:', error);
    }
  }, [calculatorName, category]);

  // Handle notification signup (placeholder)
  const handleNotificationSignup = () => {
    try {
      const analytics = getAnalytics();
      logEvent(analytics, 'notification_signup', {
        calculator_name: calculatorName
      });
      
      // Show alert for now - in a real implementation, this would connect to a notification system
      alert('Thank you for your interest! We\'ll notify you when this calculator is available.');
    } catch (error) {
      console.error('Analytics error:', error);
    }
  };

  // Get category path
  const getCategoryPath = () => {
    switch (category.toLowerCase()) {
      case 'financial':
        return '/financial';
      case 'math':
        return '/math';
      case 'fitness':
        return '/fitness';
      default:
        return '/other';
    }
  };

  return (
    <Box>
      <Card elevation={3}>
        <CardContent>
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <ConstructionIcon sx={{ fontSize: 60, color: 'warning.main', mb: 2 }} />
            
            <Typography variant="h4" component="h1" gutterBottom>
              {calculatorName} - Coming Soon!
            </Typography>
            
            <Typography variant="subtitle1" color="text.secondary" paragraph>
              We're working hard to bring you this calculator. Expected release: {expectedRelease}
            </Typography>
            
            <Button
              variant="contained"
              color="primary"
              startIcon={<NotificationsActiveIcon />}
              onClick={handleNotificationSignup}
              sx={{ mt: 2 }}
            >
              Notify Me When Available
            </Button>
          </Box>
          
          <Divider sx={{ my: 4 }} />
          
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Paper elevation={1} sx={{ p: 3, height: '100%' }}>
                <Typography variant="h6" gutterBottom>
                  About This Calculator
                </Typography>
                
                <Typography variant="body1" paragraph>
                  {description}
                </Typography>
                
                {features.length > 0 && (
                  <>
                    <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
                      Planned Features:
                    </Typography>
                    
                    <ul>
                      {features.map((feature, index) => (
                        <li key={index}>
                          <Typography variant="body2">{feature}</Typography>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </Paper>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Paper elevation={1} sx={{ p: 3, height: '100%' }}>
                <Typography variant="h6" gutterBottom>
                  Try Our Other Calculators
                </Typography>
                
                {relatedCalculators.length > 0 ? (
                  <Box component="ul" sx={{ pl: 2 }}>
                    {relatedCalculators.map((calc, index) => (
                      <li key={index}>
                        <Typography variant="body1" component={Link} to={calc.path} sx={{ 
                          color: 'primary.main',
                          textDecoration: 'none',
                          '&:hover': { textDecoration: 'underline' }
                        }}>
                          {calc.name}
                        </Typography>
                      </li>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Check out our other calculators in the {category} category.
                  </Typography>
                )}
                
                <Button
                  variant="outlined"
                  component={Link}
                  to={getCategoryPath()}
                  startIcon={<ArrowBackIcon />}
                  sx={{ mt: 3 }}
                >
                  Back to {category.charAt(0).toUpperCase() + category.slice(1)} Calculators
                </Button>
              </Paper>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      
      <Box sx={{ mt: 4 }}>
        <Paper elevation={1} sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Why We're Building This Calculator
          </Typography>
          
          <Typography variant="body1">
            At SuperCalculator, we're committed to providing you with the most comprehensive and user-friendly calculators.
            The {calculatorName} is being developed with input from experts and users like you to ensure it meets your needs.
            We appreciate your patience as we work to deliver a high-quality tool that will help you make informed decisions.
          </Typography>
        </Paper>
      </Box>
    </Box>
  );
};

export default ComingSoonCalculator;
